import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MenuItem } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { Observable, take } from 'rxjs';
import { FileModel } from '../../../models/file.model';
import { AlertService } from '../../../services/alert.service';
import { FileService } from '../../../services/file.service';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-file-card',
  templateUrl: './file-card.component.html',
  styleUrl: './file-card.component.scss',
  standalone: false,
})
export class FileCardComponent implements OnInit {
  @Input() file: FileModel;
  @Input() allowDelete: boolean = true;
  @Output() fileDeleted: EventEmitter<FileModel> =
    new EventEmitter<FileModel>();

  @ViewChild('fileMenu') fileMenu: ElementRef;

  imageUrl: string = '';

  public fileMenuItems: MenuItem[] = [
    {
      label: 'Herunterladen',
      icon: 'fa-regular fa-arrow-down-to-line',
      command: () => {
        this.fileService.downloadFile(this.file);
      },
    },
    {
      label: 'Drucken',
      icon: 'fa-regular fa-print',
      command: () => {
        this.fileService.openFile(this.file);
      },
    },
    {
      label: 'Öffnen',
      icon: 'fa-regular fa-arrow-up-right-from-square',
      command: () => {
        this.fileService.openFile(this.file);
      },
    },
  ];

  loadingFileId$: Observable<number | null>;

  constructor(
    public fileService: FileService,
    private dialogService: DialogService,
    private alertService: AlertService
  ) {
    this.loadingFileId$ = this.fileService.loadingFile$;
  }

  ngOnInit(): void {
    if (this.allowDelete) {
      this.fileMenuItems.push({
        label: 'Löschen',
        icon: 'fa-regular fa-trash-can',
        command: () => {
          this.deleteFile(this.file);
        },
      });
    }

    if (this.isFileImage) {
      this.setImageUrl(this.file);
    }
  }

  get isFileDocument(): boolean {
    return this.file.type.startsWith('application/');
  }

  get isFileImage(): boolean {
    return this.file.type.startsWith('image/');
  }

  getDocumentIcon(fileType: string): string {
    if (fileType.includes('pdf')) {
      return '/assets/icons/pdf-icon.svg';
    } else if (fileType.includes('msword')) {
      return '/assets/icons/doc-icon.svg';
    } else if (
      fileType.includes(
        'vnd.openxmlformats-officedocument.wordprocessingml.document'
      )
    ) {
      return '/assets/icons/docx-icon.svg';
    } else if (fileType.includes('ms-excel')) {
      return '/assets/icons/xls-icon.svg';
    } else if (fileType.includes('spreadsheetml.sheet')) {
      return '/assets/icons/xlsx-icon.svg';
    } else if (fileType.includes('csv')) {
      return '/assets/icons/csv-icon.svg';
    } else if (fileType.includes('mp4')) {
      return '/assets/icons/mp4-icon.svg';
    } else if (fileType.includes('mov')) {
      return '/assets/icons/mov-icon.svg';
    } else if (
      fileType.includes('powerpoint') ||
      fileType.includes('presentationml.presentation')
    ) {
      return '/assets/icons/ppt-icon.svg';
    }

    return '/assets/icons/pdf-icon.svg';
  }

  deleteFile(file: FileModel): void {
    const dialogRef = this.dialogService.open(ConfirmDialogComponent, {
      width: '475px',
      contentStyle: { overflow: 'auto' },
      breakpoints: {
        '640px': '90vw',
      },
      data: {
        title: 'Anhang löschen',
        message:
          'Bist du sicher, dass du diesen Anhang löschen möchtest? Diese Aktion kann nicht rückgängig gemacht werden.',
        icon: 'fa-regular fa-trash-can',
        iconColor: 'red',
        confirmButtonText: 'Löschen',
      },
    });

    dialogRef.onClose.pipe(take(1)).subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.fileService
          .deleteFile(file.id)
          .pipe(take(1))
          .subscribe((success: boolean) => {
            if (success) {
              this.alertService.showSuccessAlert('Anhang erfolgreich gelöscht');
              this.fileDeleted.emit(file);
            }
          });
      }
    });
  }

  private setImageUrl(file: FileModel): void {
    this.fileService
      .getFile(file.id)
      .pipe(take(1))
      .subscribe(fileBlob => {
        const reader = new FileReader();
        reader.readAsDataURL(fileBlob);
        reader.onloadend = () => {
          this.imageUrl = reader.result as string;
        };
      });
  }
}
